import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Global store with persistence for trip data
const useStore = create(
  persist(
    (set, get) => ({
      // ==================== USER STATE ====================
      user: null,
      isSignedIn: false,
      isUserLoading: true,
      
      setUser: (user) => set({ user, isSignedIn: !!user, isUserLoading: false }),
      clearUser: () => set({ user: null, isSignedIn: false, isUserLoading: false }),
      setUserLoading: (loading) => set({ isUserLoading: loading }),

      // ==================== NAVIGATION STATE ====================
      currentState: '',
      currentCity: '',
      currentPlace: '',

      // ==================== MULTI-CLUSTER (HOTEL HOPPING) ====================
      // selectedClusters: [{ name, color }] — currentCity always mirrors
      // selectedClusters[0].name so existing consumers keep working.
      // hotelHopping: "auto" (one hotel per area) | "single" (user opted out)
      selectedClusters: [],
      hotelHopping: 'auto',

      setSelectedClusters: (clusters) =>
        set({
          selectedClusters: clusters,
          currentCity: clusters[0]?.name || get().currentCity,
        }),
      setHotelHopping: (mode) => set({ hotelHopping: mode }),
      clearSelectedClusters: () => set({ selectedClusters: [], hotelHopping: 'auto' }),

      setNavigation: (state, city, place = '') => {
        const current = get();
        // Only clear trip data if STATE changed (different destination)
        // Preserve places when switching clusters (cities) within same state
        if (current.currentState !== state) {
          // Only clear if there was a previous destination set
          if (current.currentState && current.currentCity) {
            // Update state with cleared data
            set({
              currentState: state,
              currentCity: city,
              currentPlace: place,
              savedPlaces: [],
              savedPlacesWithStart: [],
              generatedItinerary: null,
              tripCentroid: null,
              nearbyHotels: [],
              selectedClusters: [],
              hotelHopping: 'auto',
            });
            return;
          }
        }
        // Same state or first time - just update navigation (keep places for cluster switches)
        set({ currentState: state, currentCity: city, currentPlace: place });
      },
      clearNavigation: () =>
        set({ currentState: '', currentCity: '', currentPlace: '', selectedClusters: [], hotelHopping: 'auto' }),

      // ==================== TRIP FORM DATA ====================
      tripFormData: {
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        companions: '',
        travellers: 0,
        meal: 'Vegetarian',
        lateNight: 'No',
        budget: { min: 200, max: 5000 },
      },

      setTripFormData: (data) => 
        set({ tripFormData: { ...get().tripFormData, ...data } }),
      
      updateTripField: (field, value) => 
        set({ tripFormData: { ...get().tripFormData, [field]: value } }),
      
      clearTripFormData: () => 
        set({
          tripFormData: {
            startDate: null,
            endDate: null,
            startTime: null,
            endTime: null,
            companions: '',
            travellers: 0,
            meal: 'Vegetarian',
            lateNight: 'No',
            budget: { min: 200, max: 5000 },
          }
        }),

      // ==================== SELECTED PLACES ====================
      savedPlaces: [],
      savedPlacesWithStart: [], // Places array including starting point
      
      addPlace: (place) => 
        set({ savedPlaces: [...get().savedPlaces, place] }),
      
      removePlace: (placeId) => 
        set({ savedPlaces: get().savedPlaces.filter(p => p._id !== placeId) }),
      
      updatePlace: (placeId, updates) => 
        set({
          savedPlaces: get().savedPlaces.map(p => 
            p._id === placeId ? { ...p, ...updates } : p
          )
        }),
      
      setSavedPlaces: (places) => 
        set({ savedPlaces: places }),
      
      setSavedPlacesWithStart: (places) =>
        set({ savedPlacesWithStart: places }),
      
      clearSavedPlaces: () => 
        set({ savedPlaces: [], savedPlacesWithStart: [] }),

      // ==================== ITINERARY STATE ====================
      generatedItinerary: null,
      isGeneratingItinerary: false,
      tripCentroid: null,
      nearbyHotels: [],
      selectedHotel: null,
      hotelSelectionStatus: null,
      travelTimeMatrix: null,
      distanceMatrix: null,
      
      setGeneratedItinerary: (itinerary) => 
        set({ generatedItinerary: itinerary }),
      
      setIsGeneratingItinerary: (status) => 
        set({ isGeneratingItinerary: status }),
      
      setTripCentroid: (centroid) =>
        set({ tripCentroid: centroid }),
      
      setNearbyHotels: (hotels) =>
        set({ nearbyHotels: hotels }),

      setSelectedHotel: (hotel) =>
        set({ selectedHotel: hotel }),

      setHotelSkipped: () =>
        set({ hotelSelectionStatus: 'skipped' }),
      
      setTravelTimeMatrix: (matrix) =>
        set({ travelTimeMatrix: matrix }),
      
      setDistanceMatrix: (matrix) =>
        set({ distanceMatrix: matrix }),
      
      clearItinerary: () => 
        set({ 
          generatedItinerary: null, 
          isGeneratingItinerary: false,
          tripCentroid: null,
          nearbyHotels: [],
          selectedHotel: null,
          hotelSelectionStatus: null,
          travelTimeMatrix: null,
          distanceMatrix: null,
        }),

      // ==================== PENDING USER ACTIVITY ====================
      // Used to preserve user's progress when they need to login
      pendingUserActivity: null,
      
      setPendingUserActivity: (activity) =>
        set({ pendingUserActivity: activity }),
      
      clearPendingUserActivity: () =>
        set({ pendingUserActivity: null }),
      
      // Restore pending activity after user logs in
      restorePendingUserActivity: () => {
        const activity = get().pendingUserActivity;
        if (!activity) return false;
        
        const updates = {};
        
        if (activity.tripFormData) {
          updates.tripFormData = { ...get().tripFormData, ...activity.tripFormData };
        }
        if (activity.journimState) {
          updates.currentState = activity.journimState;
        }
        if (activity.journimCity) {
          updates.currentCity = activity.journimCity;
        }
        if (activity.journimPlace) {
          updates.currentPlace = activity.journimPlace;
        }
        if (activity.journimStartDate || activity.journimEndDate) {
          updates.tripFormData = {
            ...(updates.tripFormData || get().tripFormData),
            startDate: activity.journimStartDate || null,
            endDate: activity.journimEndDate || null,
          };
        }
        if (activity.savedPlaces && activity.savedPlaces.length > 0) {
          updates.savedPlaces = activity.savedPlaces;
        }
        
        // Apply all updates and clear pending activity
        set({ ...updates, pendingUserActivity: null });
        return true;
      },

      // ==================== WISHLIST STATE ====================
      wishlist: [],
      wishlistLoaded: false, // Track if wishlist has been loaded from API
      
      // Set entire wishlist from API data
      setWishlist: (items) => 
        set({ wishlist: items, wishlistLoaded: true }),
      
      // Mark wishlist as loaded (even if empty)
      setWishlistLoaded: (loaded) => 
        set({ wishlistLoaded: loaded }),
      
      addToWishlist: (trip) => {
        const exists = get().wishlist.some(t => t.id === trip.id);
        if (!exists) {
          set({ wishlist: [...get().wishlist, trip] });
        }
      },
      
      removeFromWishlist: (tripId) => 
        set({ wishlist: get().wishlist.filter(t => t.id !== tripId) }),
      
      isInWishlist: (tripId) => 
        get().wishlist.some(t => t.id === tripId),
      
      toggleWishlist: (trip) => {
        const exists = get().wishlist.some(t => t.id === trip.id);
        if (exists) {
          set({ wishlist: get().wishlist.filter(t => t.id !== trip.id) });
        } else {
          set({ wishlist: [...get().wishlist, trip] });
        }
      },
      
      clearWishlist: () => 
        set({ wishlist: [], wishlistLoaded: false }),

      // ==================== YOUR TRIPS STATE ====================
      yourTrips: [],
      yourTripsLoaded: false,
      
      setYourTrips: (trips) => 
        set({ yourTrips: trips, yourTripsLoaded: true }),
      
      setYourTripsLoaded: (loaded) => 
        set({ yourTripsLoaded: loaded }),
      
      addToYourTrips: (trip) => {
        const exists = get().yourTrips.some(t => t.id === trip.id || t.tripId === trip.tripId);
        if (!exists) {
          set({ yourTrips: [...get().yourTrips, trip] });
        }
      },
      
      removeFromYourTrips: (tripId) => 
        set({ yourTrips: get().yourTrips.filter(t => t.id !== tripId && t.tripId !== tripId) }),
      
      clearYourTrips: () => 
        set({ yourTrips: [], yourTripsLoaded: false }),

      // ==================== GLOBAL ACTIONS ====================
      // Track visited pages to prevent loading screen from showing again
      visitedPages: [],
      
      markPageVisited: (pageName) => {
        const visited = get().visitedPages;
        if (!visited.includes(pageName)) {
          set({ visitedPages: [...visited, pageName] });
        }
      },
      
      hasVisitedPage: (pageName) => get().visitedPages.includes(pageName),
      
      clearVisitedPages: () => set({ visitedPages: [] }),

      // Load trip data from wishlist/yourTrips into current session
      loadTripData: (tripData) => {
        const itinerary = tripData.itineraryData;
        const clusters = itinerary?.clusters || [];
        const isMulti = clusters.length >= 2;
        const hotelHoppingMode = itinerary?.hotelHopping || (isMulti ? 'auto' : 'single');
        const colors = ["#2ec3d6", "#ff9800", "#9c27b0", "#e91e63", "#4caf50", "#00bcd4", "#ffc107", "#3f51b5"];

        set({
          currentState: tripData.stateName || '',
          currentCity: tripData.cityName || '',
          tripFormData: {
            ...get().tripFormData,
            startDate: tripData.startDate || null,
            endDate: tripData.endDate || null,
          },
          generatedItinerary: itinerary || null,
          savedPlacesWithStart: itinerary?.savedPlaces || [],
          // Restore selectedClusters and hotelHopping so editing doesn't drop them
          selectedClusters: isMulti 
            ? clusters.map((c, idx) => ({ name: c.name, color: colors[idx % colors.length] })) 
            : [],
          hotelHopping: hotelHoppingMode,
        });
      },

      resetAllData: () => {
        set({
          currentState: '',
          currentCity: '',
          currentPlace: '',
          tripFormData: {
            startDate: null,
            endDate: null,
            startTime: null,
            endTime: null,
            companions: '',
            travellers: 0,
            meal: 'Vegetarian',
            lateNight: 'No',
            budget: { min: 200, max: 5000 },
          },
          savedPlaces: [],
          savedPlacesWithStart: [],
          generatedItinerary: null,
          isGeneratingItinerary: false,
          tripCentroid: null,
          nearbyHotels: [],
          selectedHotel: null,
          hotelSelectionStatus: null,
          travelTimeMatrix: null,
          distanceMatrix: null,
          pendingUserActivity: null,
          wishlist: [],
          wishlistLoaded: false,
          yourTrips: [],
          yourTripsLoaded: false,
        });
      },
    }),
    {
      name: 'journim-storage', // localStorage key
      partialize: (state) => ({
        // Only persist trip data, not user or loading states
        // Note: wishlistLoaded is NOT persisted - we always re-fetch from API on load
        // Note: travelTimeMatrix/distanceMatrix are NOT persisted — they're always a
        // duplicate of generatedItinerary.travelMatrix/.distanceMatrix (set together
        // from the same response in AgentPage), so persisting both wrote the same
        // N×N matrix to localStorage twice on every itinerary generation.
        currentState: state.currentState,
        currentCity: state.currentCity,
        currentPlace: state.currentPlace,
        tripFormData: state.tripFormData,
        savedPlaces: state.savedPlaces,
        savedPlacesWithStart: state.savedPlacesWithStart,
        generatedItinerary: state.generatedItinerary,
        tripCentroid: state.tripCentroid,
        nearbyHotels: state.nearbyHotels,
        selectedHotel: state.selectedHotel,
        hotelSelectionStatus: state.hotelSelectionStatus,
        pendingUserActivity: state.pendingUserActivity,
        wishlist: state.wishlist,
        yourTrips: state.yourTrips,
        selectedClusters: state.selectedClusters,
        hotelHopping: state.hotelHopping,
      }),
    }
  )
);

export default useStore;
